import { inject, injectable } from 'inversify';
import { stringify } from "querystring";
import * as moment from 'moment';
import * as uuid from "uuid";
import { toJS } from 'mobx';

import { HttpService } from 'src/core/services/http.service';

@injectable()
export class CalculatePriceService {
	constructor(
		@inject(HttpService)
		private httpService: HttpService
	) { }

	private baseUrl = 'api/v1/products/search';

	async getUpdatedEntry(quoteEntry: QuoteEntryItem, supplementsDiscounts: QuoteEntryItem[], paxes: QuotePaxItem[], marketId: number, bookingDate: Date, languageCode: string, searchInCache: boolean):
		Promise<{ resultSelection: ResultSelection, search: Search, lostSupplementsDiscounts: QuoteEntryItem[] }> {
		const occupancy = this.getOccupancy(quoteEntry, paxes);
		const api: Api = {
			apiName: quoteEntry.dynamicProperties.productProviderApiName,
			name: quoteEntry.dynamicProperties.productProviderApiName,
			isActive: true,
			isActiveForApi: true
		};
		const search = this.getSearchParameterFromEntry(quoteEntry, api, occupancy, searchInCache);
		const singleProductResult = await this.searchSingleProduct(search, quoteEntry.productType, marketId, bookingDate, languageCode, null);
		if (singleProductResult == null) {
			return null;
		}

		const { parent, result } = singleProductResult;
		const selected = {
			result,
			parent,
			id: result.id,
			unitIndex: 1,
			cost: result.costPrice[1],
			sell: result.sellPrice[1]
		};
		const supDisc = result.subProducts
			.filter((m: SupplementSearchResult) => m.isRequired || supplementsDiscounts.some(p => p.productId === m.product.internalId))
			.map(m => ({
				id: m.id,
				unitIndex: 1,
				cost: m.costPrice[1],
				sell: m.sellPrice[1],
				result: m,
				parent: result
			}));

		return {
			search,
			resultSelection: {
				selected,
				supDisc
			},
			lostSupplementsDiscounts: supplementsDiscounts.filter(m => supDisc.every(p => p.result.product.internalId !== m.productId))
		};
	}

	async searchAccommodation(item: SearchAccommodation, marketId: number, bookingDate: Date, languageCode: string, preferences: SearchPreferences, config?: {}) {
		const parameters = {
			...this.getCommonParameters(item, marketId, bookingDate, languageCode),
			regionId: item.regionId,
			placeId: item.placeId,
		};

		return this.makeRequest(`${this.baseUrl}/accommodation`, parameters, preferences, config);
	}

	async searchCarRent(item: SearchCarRent, marketId: number, bookingDate: Date, languageCode: string, preferences: SearchPreferences, config?: {}) {
		const parameters = {
			...this.getCommonParameters(item, marketId, bookingDate, languageCode),
			regionId: item.regionId,
			pickupPlaceId: item.fromPlaceId,
			dropoffPlaceId: item.toPlaceId,
		};

		return this.makeRequest(`${this.baseUrl}/car-rent`, parameters, preferences, config);

	}

	async searchGeneral(item: SearchGeneral, marketId: number, bookingDate: Date, languageCode: string, preferences: SearchPreferences, config?: {}) {
		const parameters = {
			...this.getCommonParameters(item, marketId, bookingDate, languageCode),
			regionId: item.regionId,
		};

		return this.makeRequest(`${this.baseUrl}/general`, parameters, preferences, config);
	}

	async searchTour(item: SearchTour, marketId: number, bookingDate: Date, languageCode: string, preferences: SearchPreferences, config?: {}) {
		const parameters = {
			...this.getCommonParameters(item, marketId, bookingDate, languageCode),
			regionId: item.regionId,
		};

		return this.makeRequest(`${this.baseUrl}/tours`, parameters, preferences, config);
	}

	async searchTransfer(item: SearchTransfer, marketId: number, bookingDate: Date, languageCode: string, preferences: SearchPreferences, config?: {}) {
		item.endDate = item.startDate;
		const parameters = {
			...this.getCommonParameters(item, marketId, bookingDate, languageCode),
			regionId: item.regionId,
			fromPlaceId: item.fromPlaceId,
			toPlaceId: item.toPlaceId,
			returnDate: item.returnDate
		};

		return this.makeRequest(`${this.baseUrl}/transfer`, parameters, preferences, config);

	}

	async searchAutoConnectTransfer(
		parameters: SearchAutoConnetTransfer[],
		marketId: number,
		bookingDate: Date,
		languageCode: string,
		preferences: SearchPreferences
	) {
		let params = parameters.map(item => ({
			title: item.title,
			fromEntryId: item.fromEntryId,
			toEntryId: item.toEntryId,
			transferParameter: {
				...this.getCommonParameters(item.transferParam, marketId, bookingDate, languageCode),
				regionId: item.transferParam.regionId,
				fromPlaceId: item.transferParam.fromPlaceId,
				toPlaceId: item.transferParam.toPlaceId,
				returnDate: item.transferParam.returnDate
			}
		}))
		let url = `${this.baseUrl}/autoConnetTransfer`;
		if (preferences) {
			url += `?${stringify(preferences)}`;
		}
		const response = await this.httpService.post(url, params);
		return response.data as ApiResultAutoConnectTransfer;
	}

	async searchFlight(item: SearchFlight, marketId: number, bookingDate: Date, languageCode: string, preferences: SearchPreferences, config?: {}) {
		let apiName = item.apiNames
			.filter(x => x.isActive)
			.map(x => x.apiName);
		const paxs = item.occupancies.map(e => toJS(e.paxs));
		const parameter = {
			flights: item.flights.map(f => ({
				startDate: f.startDate,
				endDate: f.endDate,
				directOnly: item.directOnly,
				maxStopOverCount: item.nonStopOverCount,
				maxStopOverDurationInMinutes: item.maxStopOverDurationInMinutes,
				maxFlightDurationInMinutes: item.flightDurationInMinutes,
				fromPlaceId: f.fromPlaceId,
				toPlaceId: f.toPlaceId,
				airlineId: f.airlineId,
			})),
			publicFares: item.publicFares,
			negociatedFares: item.negociatedFares,
			lastTicketingDate: item.lastTicketingDate,
			languageCode,
			paxs: paxs.reduce((prev, curr) => [...prev, ...curr], []),
			bookingDate,
			marketId,
			apiServiceName: apiName,
			isActive: item.isActive,
			productId: item.productId,
			productCategoryId: item.productCategoryId,
			productServiceTypeId: item.productServiceTypeId,
			searchInCache: item.searchInCache,
			searchId: item.searchId,
			directOnly: item.directOnly,
			nonStopOverCount: item.nonStopOverCount,
		}

		return this.makeRequest(`${this.baseUrl}/flight`, parameter, preferences, config);
	}

	async searchSimulation(item: SearchSimulation, marketId: number, bookingDate: Date, languageCode: string, preferences: SearchPreferences) {
		let api: Api = { apiName: 'ProductBookingSearchService', name: 'Bookem', isActive: true, isActiveForApi: true };

		const parameters = this.getCommonParameters(item, marketId, bookingDate, languageCode);
		parameters.apiServiceName = [api.apiName]
		let result = await this.makeRequest(`${this.baseUrl}/simulation`, parameters, preferences);
		return result;
	}

	getOccupancy(item: QuoteEntryItem, paxs: QuotePaxItem[]): Occupancy[] {
		const occupancy: Occupancy[] = [
			{
				unit: 1,
				paxs: item.dynamicProperties.occupancy
					.map(p => {
						const pax = paxs.find(e => e.id === p.id);
						return pax
							? {
								id: p.id,
								name: pax.formalName,
								age: moment.utc(item.dateFrom).diff(moment(pax.dynamicProperties['birthDate']), 'year')
							}
							: undefined;
					})
					.filter(p => p != undefined),
			}
		];

		return occupancy;
	}

	getSearchParameterFromEntry(entry: QuoteEntryItem, api: Api, occupancy: Occupancy[], searchInCache: boolean): Search {
		return this.createSearchRequest(entry.productType, [api], searchInCache, {
			productId: entry.productId,
			productExternalId: entry.externalId,
			productServiceTypeId: entry.dynamicProperties.productServiceTypeId ? entry.dynamicProperties.productServiceTypeId : null,
			dateFrom: entry.dateFrom,
			dateTo: entry.dateTo,
			occupancies: occupancy,
			additionalProperties: entry.dynamicProperties.extraProperties,
		});
	}

	getSelectedSubProducts(subProducts: ProductSearchResult[]): ProductSearchResult[] {
		const result: ProductSearchResult[] = [];
		for (const product of subProducts) {
			if (product.productType === 6 && (<SupplementSearchResult>product).isRequired /*|| (<SupplementSearchResult>product).selectedByUser*/) {
				result.push(product);
			}
		}
		return result;
	}

	getSelectedPrice(item: { [occupancyIndex: number]: Money }): Money {
		for (const index of Object.getOwnPropertyNames(item)) {
			const price = item[parseInt(index)];
			//if (price.selectedByUser) {
			return price;
			//}
		}
	}

	private getCommonParameters(item: Search & SearchDates, marketId: number, bookingDate: Date, languageCode: string) {
		const apiNames = item.apiNames
			.filter(x => x.isActive)
			.map(x => x.apiName);

		return {
			useSellMarginPolicies: item.useSellMarginPolicies,
			contractId: item.contractId,
			supplierId: item.supplierId,
			productId: item.productId,
			productExternalId: item.productExternalId,
			languageCode: languageCode,
			units: item.occupancies,
			bookingDate: bookingDate,
			productCategoryId: item.productCategoryId,
			productServiceTypeId: item.productServiceTypeId,
			marketId: marketId,
			apiServiceName: apiNames,
			isActive: item.isActive,
			searchInCache: item.searchInCache,
			searchId: item.searchId,
			startDate: item.startDate,
			endDate: item.endDate,
			type: item.type,
			taxonomies: item.taxonomies,
			facets: item.facets.map(x => ({
				name: x.name,
				multiple: x.multiple,
				headings: x.headings.map(h => ({
					value: h.value,
					valueField: h.valueField,
					matchCount: h.matchCount,
					rangeDate: h.rangeDate,
					rangeDecimal: h.rangeDecimal,
					subHeading: h.subHeading

				}))
			})).filter(f => f.headings.length > 0),
			additionalProperties: item.additionalProperties,
		};
	}

	private async makeRequest(url: string, parameters: any, preferences: SearchPreferences, config?: {}) {
		if (preferences) {
			url += `?${stringify(preferences)}`;
		}
		const request = this.httpService.post(url, parameters, config);
		const response = await request;
		return response.data as ApiResult;
	}

	private makeProductSearch(type: 'Accommodation' | 'CarRent' | 'General' | 'Transfer', apiNames: Api[], productParameters: ProductParameters, searchInCache: boolean): Search & SearchDates {
		return {
			apiNames,
			type,
			searchInCache,
			searchId: uuid.v4(),
			isActive: true,
			occupancies: productParameters.occupancies,
			productCategoryId: null,
			productId: productParameters.productId,
			productExternalId: productParameters.productExternalId,
			supplierId: productParameters.supplierId,
			startDate: productParameters.dateFrom,
			endDate: productParameters.dateTo,
			productServiceTypeId: productParameters.productServiceTypeId,
			facets: [],
			additionalProperties: productParameters.additionalProperties,
		};
	}

	async searchSingleProduct(
		search: Search,
		productType: number,
		marketId: number,
		bookingDate: Date,
		languageCode: string,
		preferences: SearchPreferences
	): Promise<SingleProductResult> {
		let apiResults: ApiResult;
		switch (productType) {
			case 0: // General
				apiResults = await this.searchGeneral(search as SearchGeneral, marketId, bookingDate, languageCode, preferences);
				break;
			case 1: // Accommodation
			case 5: // Room
				apiResults = await this.searchAccommodation(search as SearchAccommodation, marketId, bookingDate, languageCode, preferences);
				break;
			case 2: // CarRent
				apiResults = await this.searchCarRent(search as SearchCarRent, marketId, bookingDate, languageCode, preferences);
				break;
			case 3: // Transfer
				apiResults = await this.searchTransfer(search as SearchTransfer, marketId, bookingDate, languageCode, preferences);
				break;
			case 4: // Flight
				apiResults = await this.searchFlight(search as SearchFlight, marketId, bookingDate, languageCode, preferences);
				break;
			default:
				throw 'Unexpected product type!';
		}

		if (apiResults.items == null || apiResults.items.length !== 1 || apiResults.items[0] === null) {
			return null;
		}

		const productSearchResult = apiResults.items[0];
		let parent: ProductSearchResult = null;
		let result: ProductSearchResult;
		if ((search.productId && productSearchResult.product.internalId === search.productId) || (search.productExternalId && productSearchResult.product.externalId === search.productExternalId)) {
			result = productSearchResult;
		} else if (productSearchResult.subProducts == null) {
			return null;
		} else { // if the SearchResult is not the product then it must have it as a subproduct (eg.: a Room in a Hotel)
			parent = productSearchResult;
			result = productSearchResult.subProducts.find(m => m.product.internalId === search.productId || m.product.externalId === search.productExternalId);
		}

		return { parent, result };
	}

	createSearchRequest(productType: number, apis: Api[], searchInCache: boolean, productParameters: ProductParameters): Search {
		switch (productType) {
			case 0: // General
				const generalRequest = this.makeProductSearch('General', apis, productParameters, searchInCache);
				generalRequest.startDate = moment.utc(generalRequest.startDate).startOf("day").toDate();
				generalRequest.endDate = moment.utc(generalRequest.endDate).startOf("day").toDate();
				return generalRequest;
			case 1: // Accommodation
			case 5: // Room
				const accommodationRequest = this.makeProductSearch('Accommodation', apis, productParameters, searchInCache);
				accommodationRequest.startDate = moment.utc(accommodationRequest.startDate).startOf("day").toDate();
				accommodationRequest.endDate = moment.utc(accommodationRequest.endDate).startOf("day").toDate();
				return {
					...accommodationRequest,
					bypassOcuppancy: false,
					regionId: null,
					nights: 1,
					placeId: null,
				} as SearchAccommodation;
			case 2: // CarRent
				return {
					...this.makeProductSearch('CarRent', apis, productParameters, searchInCache),
					regionId: null,
					fromPlaceId: null,
					toPlaceId: null,
					days: 1,
				} as SearchCarRent;
			case 3: // Transfer
				return {
					...this.makeProductSearch('Transfer', apis, productParameters, searchInCache),
					fromPlaceId: null,
					toPlaceId: null,
					returnJourney: false,
				} as SearchTransfer;
			case 4: // Flight
				const flights: SearchFlightLeg[] = [
					{
						nights: 1,
						id: '1',
						startDate: productParameters.dateFrom,
						endDate: productParameters.dateTo
					}];

				return {
					type: 'Flights',
					isActive: true,
					flights: flights,
					occupancies: productParameters.occupancies,
					lastTicketingDate: new Date(),
					nonStopOverCount: 0,
					negociatedFares: false,
					directOnly: false,
					publicFares: false,
					apiNames: apis,
					productId: productParameters.productId,
					productExternalId: productParameters.productExternalId,
					additionalProperties: productParameters.additionalProperties,
				} as SearchFlight;
			default:
				throw 'Unexpected product type!';
		}
	}
}

interface ProductParameters {
	productId?: number;
	productExternalId: string;
	supplierId?: number;
	productServiceTypeId?: number;
	dateFrom: Date;
	dateTo: Date;
	occupancies: Occupancy[];
	additionalProperties: { [key: string]: string };
}

interface SingleProductResult {
	parent: ProductSearchResult;
	result: ProductSearchResult;
}



// WEBPACK FOOTER //
// ./src/app/bookings/shared/calculate-prices.service.ts